import { useMemo, useRef, useState } from 'react';
import {
  AudioLines,
  ChevronDown,
  ChevronUp,
  FileAudio,
  Hash,
  Loader2,
  MicVocal,
  Pause,
  Play,
  Square,
  Trash2,
  Type,
  Variable as VariableIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { cn } from '@/lib/utils/cn';
import { usePromptUploads, useTtsTypes } from '@/features/voice-campaigns/api';
import { useUserProfile } from '@/features/auth/api';
import type {
  TtsConvertRequest,
  TtsConvertResponse,
} from '@/features/voice-library/types';
import type { TtsEditorMode, TtsFlowFormValues, TtsRow } from '../../types';

interface Props {
  row: TtsRow;
  index: number;
  totalRows: number;
  values: TtsFlowFormValues;
  /** Patch this single row in the values.ttsRows array. */
  onPatch: (patch: Partial<TtsRow>) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
}

const VARIABLE_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Returns the editor-side mode for a given row. Wire-time only knows
 *  prompt | variable; the editor adds a synthetic `text` mode.
 *
 *  A brand-new text row carries `text: ''` (empty string). We test for the
 *  PRESENCE of a `text` field (`typeof row.text === 'string'`) rather than
 *  truthiness so a fresh empty text row still renders the textarea — if
 *  we checked truthiness, the textarea would never appear and typing
 *  wouldn't work.
 */
function modeOf(row: TtsRow): TtsEditorMode {
  if (row.ttsType === 'variable') return 'variable';
  if (typeof row.text === 'string') return 'text';
  return 'prompt';
}

const MODE_VISUAL: Record<
  TtsEditorMode,
  { label: string; stripe: string; chipBg: string; chipFg: string; icon: typeof Play }
> = {
  prompt: {
    label: 'Prompt',
    stripe: 'bg-sky-500',
    chipBg: 'bg-sky-100',
    chipFg: 'text-sky-700',
    icon: FileAudio,
  },
  text: {
    label: 'Text',
    stripe: 'bg-fuchsia-500',
    chipBg: 'bg-fuchsia-100',
    chipFg: 'text-fuchsia-700',
    icon: Type,
  },
  variable: {
    label: 'Variable',
    stripe: 'bg-violet-500',
    chipBg: 'bg-violet-100',
    chipFg: 'text-violet-700',
    icon: VariableIcon,
  },
};

export function TtsStepCard({
  row,
  index,
  totalRows,
  values,
  onPatch,
  onRemove,
  onMoveUp,
  onMoveDown,
}: Props) {
  const userId = useAuthStore((s) => s.userId);
  const prompts = usePromptUploads();
  const ttsTypes = useTtsTypes();
  const profile = useUserProfile();

  const mode = modeOf(row);
  const visual = MODE_VISUAL[mode];

  /* ----- Mode switching ----- */
  const setMode = (next: TtsEditorMode) => {
    if (next === 'prompt') {
      onPatch({
        ttsType: 'prompt',
        value: '',
        variableType: '',
        text: undefined,
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    } else if (next === 'text') {
      onPatch({
        ttsType: 'prompt',
        value: '',
        variableType: '',
        text: row.text ?? '',
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    } else {
      onPatch({
        ttsType: 'variable',
        value: '',
        variableType: '',
        text: undefined,
        previewUrl: undefined,
        previewTtsId: undefined,
      });
    }
  };

  /* ----- Prompt lookup (for play URL + label) ----- */
  const promptOptions = useMemo(
    () =>
      (prompts.data ?? [])
        .filter(
          (p) =>
            (p.promptCategory === 'welcome' ||
              p.promptCategory === 'menu' ||
              p.promptCategory === 'tts') &&
            (p.promptStatus === undefined ||
              Number(p.promptStatus) === 1 ||
              p.promptStatus === '1'),
        )
        .map((p) => ({
          id: String(p.promptId),
          label: p.fileName || p.promptName || `#${p.promptId}`,
          url: (p as { promptUrl?: string }).promptUrl ?? '',
        })),
    [prompts.data],
  );
  const promptById = useMemo(() => {
    const m = new Map<string, (typeof promptOptions)[number]>();
    for (const p of promptOptions) m.set(p.id, p);
    return m;
  }, [promptOptions]);

  /* ----- Variable slots — cap by user limit ----- */
  const totalVariable = Number(
    (profile.data as { totalVariable?: number } | undefined)?.totalVariable ??
      0,
  );
  // Slot options for this row: any slot that's not currently used by a
  // DIFFERENT variable row.
  const slotOptions = useMemo(() => {
    const used = new Set(
      values.ttsRows
        .filter(
          (r, i) => i !== index && r.ttsType === 'variable' && r.value !== '',
        )
        .map((r) => Number(r.value)),
    );
    const cap = totalVariable > 0 ? totalVariable : 10;
    return VARIABLE_SLOTS.filter((s) => s <= cap && !used.has(s));
  }, [values.ttsRows, index, totalVariable]);

  /* ----- Per-step audio preview ----- */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(false);
  };

  const playUrl = (url: string) => {
    stopAudio();
    const a = new Audio(url);
    a.addEventListener('ended', () => setPlaying(false));
    void a.play();
    audioRef.current = a;
    setPlaying(true);
  };

  const onPreview = async () => {
    if (playing) {
      stopAudio();
      return;
    }
    if (mode === 'prompt') {
      const url = promptById.get(row.value)?.url;
      if (!url) {
        toast.error('Pick a prompt first.');
        return;
      }
      playUrl(url);
      return;
    }
    if (mode === 'text') {
      const text = row.text?.trim();
      if (!text) {
        toast.error('Type some text first.');
        return;
      }
      // Reuse the cached preview if the text hasn't changed since the
      // last synthesis. We don't have a perfect "is dirty" signal — use
      // the presence of `previewUrl` AND assume the user re-clicked
      // intentionally to mean "play what you generated last time".
      if (row.previewUrl) {
        playUrl(row.previewUrl);
        return;
      }
      if (!values.language || !values.gender) {
        toast.error('Pick a language and a voice in step 1 first.');
        return;
      }
      setSynthesizing(true);
      try {
        const convertReq: TtsConvertRequest = {
          userId: Number(userId),
          gender: values.gender as TtsConvertRequest['gender'],
          textScript: text,
          characterCount: text.length,
          language: values.language,
          filename: `preview_${Date.now()}`,
          category: 'tts',
        };
        const res = await api.post<TtsConvertResponse>(
          '/api/obd/convertion/texttospeech',
          convertReq,
        );
        if (res.status === 'fail') {
          toast.error(res.msg || 'Could not synthesize.');
          return;
        }
        onPatch({
          previewUrl: res.downloadUrl,
          previewTtsId: res.ttsId,
        });
        playUrl(res.downloadUrl);
      } catch {
        toast.error('Could not synthesize.');
      } finally {
        setSynthesizing(false);
      }
      return;
    }
    // Variable rows can't be previewed — they hold no audio at design time.
    toast.message('Variable rows speak runtime values; no static preview.');
  };

  return (
    <div className="group/step relative flex gap-2 rounded-lg border border-border bg-card shadow-sm transition-all hover:shadow-md">
      {/* Step number rail + drag handle */}
      <div className="flex flex-col items-center justify-between border-r border-border bg-muted/30 px-2 py-2">
        <span
          className={cn(
            'inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold uppercase text-white',
            visual.stripe,
          )}
        >
          {index + 1}
        </span>
        <div className="flex flex-col gap-0.5">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            onClick={onMoveUp}
            disabled={!onMoveUp || index === 0}
            aria-label="Move up"
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-muted-foreground"
            onClick={onMoveDown}
            disabled={!onMoveDown || index === totalRows - 1}
            aria-label="Move down"
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Card body */}
      <div className="flex flex-1 flex-col gap-2 py-2 pr-2">
        {/* Mode segmented control + meta + actions */}
        <div className="flex items-start gap-2">
          <ModeSegmented mode={mode} onChange={setMode} />
          <div className="flex-1" />
          <Button
            type="button"
            size="icon"
            variant="outline"
            className={cn(
              'h-7 w-7 shrink-0 rounded-full',
              playing && 'border-primary text-primary',
            )}
            onClick={onPreview}
            disabled={synthesizing}
            aria-label={playing ? 'Stop preview' : 'Preview'}
          >
            {synthesizing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : playing ? (
              <Square className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
            onClick={onRemove}
            aria-label="Remove step"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* Mode-specific body */}
        {mode === 'prompt' && (
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">
              Pick from voice library
            </Label>
            <Select
              value={row.value}
              onValueChange={(v) =>
                onPatch({
                  value: v,
                  text: undefined,
                  previewUrl: undefined,
                  previewTtsId: undefined,
                })
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue
                  placeholder={
                    prompts.isLoading
                      ? 'Loading…'
                      : promptOptions.length === 0
                        ? 'No prompts uploaded'
                        : 'Pick a prompt'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {promptOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {mode === 'text' && (
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">
              Type what the bot should say
            </Label>
            <Textarea
              rows={2}
              value={row.text ?? ''}
              onChange={(e) =>
                onPatch({
                  text: e.target.value,
                  // Invalidate the cached preview when text changes.
                  previewUrl: undefined,
                  previewTtsId: undefined,
                })
              }
              placeholder="e.g. Press 1 for sales, 2 for support."
              className="resize-none text-sm"
            />
            <p className="text-[10px] text-muted-foreground">
              Auto-synthesized into a prompt when you save the flow. Click ▶ to
              preview without saving.
            </p>
          </div>
        )}

        {mode === 'variable' && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">
                Slot
              </Label>
              <Select
                value={row.value}
                onValueChange={(v) => onPatch({ value: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Pick slot" />
                </SelectTrigger>
                <SelectContent>
                  {slotOptions.map((s) => (
                    <SelectItem key={s} value={String(s)}>
                      Slot {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">
                Type
              </Label>
              <Select
                value={row.variableType}
                onValueChange={(v) => onPatch({ variableType: v })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Pick type" />
                </SelectTrigger>
                <SelectContent>
                  {(ttsTypes.data ?? []).map((t, i) => (
                    <SelectItem key={`${t.tts_type}-${i}`} value={t.tts_type}>
                      {t.tts_type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Mode chip (top-right floating) */}
      <span
        aria-hidden
        className={cn(
          'absolute right-2 top-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide opacity-0 transition-opacity group-hover/step:opacity-100',
          visual.chipBg,
          visual.chipFg,
        )}
      >
        <visual.icon className="h-2.5 w-2.5" />
        {visual.label}
      </span>

      {/* Silence unused import warnings — these icons are reserved for the
          right-pane preview that renders alongside this card. */}
      <span aria-hidden className="hidden">
        <AudioLines />
        <Hash />
        <MicVocal />
        <Pause />
      </span>
    </div>
  );
}

function ModeSegmented({
  mode,
  onChange,
}: {
  mode: TtsEditorMode;
  onChange: (m: TtsEditorMode) => void;
}) {
  const OPTIONS: { id: TtsEditorMode; label: string }[] = [
    { id: 'prompt', label: 'Prompt' },
    { id: 'text', label: 'Text' },
    { id: 'variable', label: 'Variable' },
  ];
  return (
    <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5">
      {OPTIONS.map((opt) => {
        const active = mode === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              'rounded px-2 py-0.5 text-[11px] font-medium transition-colors',
              active
                ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
